class ProductDetailModern extends HTMLElement {
  constructor() {
    super();

    // Store Glide instance for modal
    this.modalGlide = null;

    // Delay initialization to ensure DOM is ready
    setTimeout(() => {
      this.form = this.querySelector('form');
      this.variantSelectors = this.querySelectorAll('[data-option-value]');
      this.currentVariant = null;

      if (this.form) {
        this.init();
        // Ensure form and buttons stay visible
        this.ensureVisibility();
      }
    }, 0);
  }

  init() {
    // Check initial variant availability FIRST
    try {
      this.checkInitialVariant();
    } catch (error) {
      // Handle initialization errors silently in production
    }

    // Update size button states on init
    this.updateSizeButtonStates();

    // Move modal to body (portal pattern) - CRITICAL FIX
    this.moveModalToBody();

    // Image modal functionality
    this.initImageModal();

    // Size guide modal
    this.initSizeGuideModal();

    // Stock notify modal
    this.initStockNotifyModal();

    // Mobile slider
    this.initMobileSlider();

    // Handle resize events
    this.handleResize();

    // Variant selection - support both radio inputs and label clicks
    this.variantSelectors.forEach((selector) => {
      selector.addEventListener('click', this.onVariantChange.bind(this));
    });

    // Also listen to radio input changes directly
    this.querySelectorAll('.product-option__input').forEach((input) => {
      input.addEventListener('change', this.onVariantChange.bind(this));
    });

    // Quantity controls
    const quantityInput = this.querySelector('[data-quantity-input]');
    const minusButton = this.querySelector('[data-quantity-minus]');
    const plusButton = this.querySelector('[data-quantity-plus]');

    if (minusButton && plusButton && quantityInput) {
      minusButton.addEventListener('click', () => this.changeQuantity(-1));
      plusButton.addEventListener('click', () => this.changeQuantity(1));

      // Update button states
      this.updateQuantityButtons();
      quantityInput.addEventListener('change', () => this.updateQuantityButtons());
    }

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    // Ensure variant ID input element exists (value may be empty if preselect is off)
    const variantInput = this.form?.querySelector('[name="id"]');
    if (!variantInput) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'id';
      input.value = '';
      this.form?.appendChild(input);
    }

    // Buy now button
    const buyNowButton = this.querySelector('[data-buy-now]');
    if (buyNowButton) {
      buyNowButton.addEventListener('click', this.onBuyNow.bind(this));
    }
  }

  ensureVisibility() {
    // Ensure form and buttons stay visible
    const form = this.querySelector('form');
    const buttonsBlock = this.querySelector('.product-detail-modern__buttons-block');
    const mobileSlider = this.querySelector('.product-detail-modern__mobile-slider');

    if (form) {
      form.style.display = 'block';
      form.style.visibility = 'visible';
    }

    if (buttonsBlock) {
      buttonsBlock.style.display = 'block';
      buttonsBlock.style.visibility = 'visible';
    }

    // Hide mobile slider on desktop
    if (mobileSlider && window.innerWidth > 990) {
      mobileSlider.style.display = 'none';
      mobileSlider.style.visibility = 'hidden';
    }
  }

  handleResize() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const mobileSlider = this.querySelector('.product-detail-modern__mobile-slider');
        const desktopImages = this.querySelector('.product-detail-modern__images');

        if (window.innerWidth > 990) {
          // Desktop view
          if (mobileSlider) {
            mobileSlider.style.display = 'none';
            mobileSlider.style.visibility = 'hidden';
          }
          if (desktopImages) {
            desktopImages.style.display = 'grid';
            desktopImages.style.visibility = 'visible';
          }
          // Clear mobile slider state
          this.mobileSliderInitialized = false;
        } else {
          // Mobile view
          if (mobileSlider) {
            mobileSlider.style.display = 'block';
            mobileSlider.style.visibility = 'visible';
          }
          if (desktopImages) {
            desktopImages.style.display = 'none';
          }
          // Reinitialize mobile slider if needed
          if (!this.mobileSliderInitialized) {
            this.initMobileSlider();
            this.mobileSliderInitialized = true;
          }
        }
      }, 250);
    });
  }

  moveModalToBody() {
    // Move modal to body to escape any z-index stacking contexts
    const modal = document.getElementById('productImageModal');
    if (modal && modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
  }

  initImageModal() {
    // Image click handlers
    const images = this.querySelectorAll('[data-modal-trigger]');
    const modal = document.getElementById('productImageModal');
    const closeButton = modal?.querySelector('[data-modal-close]');

    images.forEach((img) => {
      img.addEventListener('click', () => {
        const imageIndex = parseInt(img.dataset.imageIndex);
        this.openModal(imageIndex);
      });
    });

    // Close modal
    closeButton?.addEventListener('click', () => {
      this.closeModal();
    });

    // Close on overlay click
    modal?.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.closeModal();
      }
    });

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal?.classList.contains('active')) {
        this.closeModal();
      }
    });

    // Setup zoom functionality
    this.setupImageZoom();
  }

  setupImageZoom() {
    const modal = document.getElementById('productImageModal');
    if (!modal) return;

    // Zoom state for each image
    this.zoomStates = new Map();
    this.isPanning = false;

    // Delegate event listeners
    modal.addEventListener('click', (e) => {
      // Don't toggle zoom if we were panning
      if (this.isPanning) {
        this.isPanning = false;
        return;
      }

      const img = e.target.closest('.product-image-modal__image');
      if (!img) return;

      const container = img.closest('.media');
      if (!container) return;

      this.toggleZoom(img, container);
    });

    // Touch zoom (pinch)
    modal.addEventListener('touchstart', (e) => {
      const container = e.target.closest('.media');
      if (!container || e.touches.length !== 2) return;

      this.handlePinchStart(e, container);
    }, { passive: false });

    modal.addEventListener('touchmove', (e) => {
      if (this.isPinching) {
        e.preventDefault();
        this.handlePinchMove(e);
      }
    }, { passive: false });

    modal.addEventListener('touchend', () => {
      this.isPinching = false;
      this.initialPinchDistance = null;
    });
  }

  toggleZoom(img, container) {
    const state = this.zoomStates.get(img) || { scale: 1, x: 0, y: 0 };

    if (state.scale === 1) {
      // Zoom in
      state.scale = 2.5;
      img.classList.add('is-zoomed');
      container.classList.add('is-zoomed');
      this.updateTransform(img, state);
      this.enablePan(img, container, state);
    } else {
      // Zoom out
      this.resetZoom(img, container);
    }
  }

  resetZoom(img, container) {
    const state = { scale: 1, x: 0, y: 0 };
    this.zoomStates.set(img, state);
    img.classList.remove('is-zoomed');
    container.classList.remove('is-zoomed');
    this.updateTransform(img, state);
    this.disablePan(img);
  }

  enablePan(img, container, state) {
    let isDragging = false;
    let hasMoved = false;
    let startX = 0;
    let startY = 0;
    let initialX = state.x;
    let initialY = state.y;

    const onMouseDown = (e) => {
      isDragging = true;
      hasMoved = false;
      startX = e.clientX || e.touches?.[0].clientX;
      startY = e.clientY || e.touches?.[0].clientY;
      initialX = state.x;
      initialY = state.y;
      container.style.cursor = 'grabbing';
      
      // Prevent click event propagation
      e.stopPropagation();
    };

    const onMouseMove = (e) => {
      if (!isDragging) return;
      
      const clientX = e.clientX || e.touches?.[0].clientX;
      const clientY = e.clientY || e.touches?.[0].clientY;

      const deltaX = clientX - startX;
      const deltaY = clientY - startY;

      // Check if actually moved (threshold to differentiate from click)
      if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
        hasMoved = true;
        this.isPanning = true;
        e.preventDefault();
        e.stopPropagation();

        // Calculate boundaries
        const rect = container.getBoundingClientRect();
        const imgRect = img.getBoundingClientRect();

        const maxX = Math.max(0, (imgRect.width * state.scale - rect.width) / 2);
        const maxY = Math.max(0, (imgRect.height * state.scale - rect.height) / 2);

        // Constrain pan to image boundaries
        state.x = Math.max(-maxX, Math.min(maxX, initialX + deltaX));
        state.y = Math.max(-maxY, Math.min(maxY, initialY + deltaY));

        this.updateTransform(img, state);
        this.zoomStates.set(img, state);
      }
    };

    const onMouseUp = (e) => {
      if (hasMoved) {
        // Prevent click event if we were panning
        e.preventDefault();
        e.stopPropagation();
      }
      isDragging = false;
      
      // Reset panning flag after a short delay to prevent zoom toggle
      setTimeout(() => {
        hasMoved = false;
        this.isPanning = false;
      }, 100);
      
      container.style.cursor = 'grab';
    };

    // Click handler to prevent zoom toggle during pan
    const onClick = (e) => {
      if (hasMoved || this.isPanning) {
        e.stopPropagation();
        e.preventDefault();
      }
    };

    // Store handlers for cleanup
    img._panHandlers = { onMouseDown, onMouseMove, onMouseUp, onClick };

    container.addEventListener('mousedown', onMouseDown);
    container.addEventListener('touchstart', onMouseDown, { passive: false });
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('touchmove', onMouseMove, { passive: false });
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchend', onMouseUp);
    container.addEventListener('click', onClick, true); // Use capture phase
  }

  disablePan(img) {
    const handlers = img._panHandlers;
    if (!handlers) return;

    const container = img.closest('.media');
    if (container) {
      container.removeEventListener('mousedown', handlers.onMouseDown);
      container.removeEventListener('touchstart', handlers.onMouseDown);
      container.removeEventListener('click', handlers.onClick, true);
    }

    document.removeEventListener('mousemove', handlers.onMouseMove);
    document.removeEventListener('touchmove', handlers.onMouseMove);
    document.removeEventListener('mouseup', handlers.onMouseUp);
    document.removeEventListener('touchend', handlers.onMouseUp);

    delete img._panHandlers;
  }

  handlePinchStart(e, container) {
    if (e.touches.length !== 2) return;

    this.isPinching = true;
    const touch1 = e.touches[0];
    const touch2 = e.touches[1];
    this.initialPinchDistance = this.getDistance(touch1, touch2);

    const img = container.querySelector('.product-image-modal__image');
    if (img) {
      this.currentPinchImg = img;
      this.currentPinchContainer = container;
    }
  }

  handlePinchMove(e) {
    if (!this.isPinching || e.touches.length !== 2) return;

    const touch1 = e.touches[0];
    const touch2 = e.touches[1];
    const currentDistance = this.getDistance(touch1, touch2);
    const scale = currentDistance / this.initialPinchDistance;

    const img = this.currentPinchImg;
    const container = this.currentPinchContainer;

    if (img && container) {
      let state = this.zoomStates.get(img) || { scale: 1, x: 0, y: 0 };
      state.scale = Math.max(1, Math.min(3, state.scale * scale));

      if (state.scale > 1) {
        img.classList.add('is-zoomed');
        container.classList.add('is-zoomed');
        this.enablePan(img, container, state);
      } else {
        this.resetZoom(img, container);
      }

      this.updateTransform(img, state);
      this.zoomStates.set(img, state);
      this.initialPinchDistance = currentDistance;
    }
  }

  getDistance(touch1, touch2) {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  updateTransform(img, state) {
    img.style.transform = `scale(${state.scale}) translate(${state.x / state.scale}px, ${state.y / state.scale}px)`;
  }

  openModal(startIndex = 0) {
    const modal = document.getElementById('productImageModal');
    if (!modal) {
      return;
    }

    // Reset all zooms
    modal.querySelectorAll('.product-image-modal__image').forEach(img => {
      const container = img.closest('.media');
      if (container) {
        this.resetZoom(img, container);
      }
    });

    // Show modal FIRST (important for Glide to calculate dimensions)
    modal.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Wait for modal to be visible before initializing Glide
    // This is crucial - Glide needs visible elements to calculate sizes
    requestAnimationFrame(() => {
      this.initModalGlide(startIndex);
    });
  }

  closeModal() {
    const modal = document.getElementById('productImageModal');
    if (!modal) return;

    // Reset all zooms before closing
    modal.querySelectorAll('.product-image-modal__image').forEach(img => {
      const container = img.closest('.media');
      if (container) {
        this.resetZoom(img, container);
      }
    });

    // Destroy Glide instance
    if (this.modalGlide) {
      this.modalGlide.destroy();
      this.modalGlide = null;
    }

    // Remove keyboard event listener
    if (this.modalKeydownHandler) {
      document.removeEventListener('keydown', this.modalKeydownHandler);
      this.modalKeydownHandler = null;
    }

    modal.classList.remove('active');
    document.body.style.overflow = '';
  }

  initModalGlide(startIndex = 0) {
    // Check if Glide is available
    if (typeof Glide === 'undefined') {
      return;
    }

    const glideElement = document.getElementById('productModalGlide');
    if (!glideElement) {
      return;
    }

    // Destroy existing instance if any
    if (this.modalGlide) {
      try {
        this.modalGlide.destroy();
      } catch (e) {
        // Ignore destruction errors
      }
    }

    try {
      // Initialize new Glide instance
      this.modalGlide = new Glide(glideElement, {
        type: 'carousel',
        startAt: startIndex,
        perView: 1,
        gap: 0,
        keyboard: true,
        swipeThreshold: 80,
        dragThreshold: 120,
        animationDuration: 400,
        animationTimingFunc: 'cubic-bezier(0.165, 0.840, 0.440, 1.000)',
        focusAt: 'center',
        breakpoints: {
          768: {
            peek: 0,
            perView: 1,
          },
        },
      });

      // Mount the slider
      this.modalGlide.mount();

      // Force first slide to be visible after mount
      setTimeout(() => {
        const activeSlide = glideElement.querySelector('.glide__slide--active');
        if (activeSlide) {
          activeSlide.style.display = 'flex';
          activeSlide.style.opacity = '1';
          activeSlide.style.visibility = 'visible';
        }

        // Ensure all slides are in DOM and visible
        const allSlides = glideElement.querySelectorAll('.glide__slide');
        allSlides.forEach((slide) => {
          slide.style.display = 'flex';
        });
      }, 50);
    } catch (error) {
      // Fallback: at least show the first image
      const slides = glideElement.querySelectorAll('.glide__slide');
      slides.forEach((slide, index) => {
        slide.style.display = index === startIndex ? 'flex' : 'none';
      });
    }

    // Add keyboard navigation for arrow keys
    const modal = document.getElementById('productImageModal');
    const handleKeydown = (e) => {
      if (!modal?.classList.contains('active')) return;

      if (e.key === 'ArrowLeft') {
        this.modalGlide.go('<');
      } else if (e.key === 'ArrowRight') {
        this.modalGlide.go('>');
      }
    };

    document.addEventListener('keydown', handleKeydown);

    // Store handler for cleanup
    this.modalKeydownHandler = handleKeydown;
  }

  initMobileSlider() {
    // Only initialize on mobile
    if (window.innerWidth > 990) {
      return;
    }

    const sliderContainer = this.querySelector('.product-detail-modern__mobile-slider');
    if (!sliderContainer) {
      return;
    }

    // Simple touch slider implementation
    this.setupSimpleSlider(sliderContainer);
  }

  setupSimpleSlider(container) {
    const track = container.querySelector('.simple-slider__track');
    const slides = container.querySelectorAll('.simple-slider__slide');
    const prevBtn = container.querySelector('[data-slider-prev]');
    const nextBtn = container.querySelector('[data-slider-next]');
    const dots = container.querySelectorAll('[data-slider-dot]');

    if (!track || slides.length === 0) {
      return;
    }

    let currentIndex = 0;
    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    const updateSlider = (index) => {
      currentIndex = Math.max(0, Math.min(index, slides.length - 1));
      const offset = -currentIndex * 100;
      track.style.transform = `translateX(${offset}%)`;

      // Update dots
      dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === currentIndex);
      });

      // Update buttons
      if (prevBtn) prevBtn.disabled = currentIndex === 0;
      if (nextBtn) nextBtn.disabled = currentIndex === slides.length - 1;
    };

    // Button controls with proper event handling
    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        updateSlider(currentIndex - 1);
      });

      // Prevent touch events from blocking scroll
      prevBtn.addEventListener(
        'touchstart',
        (e) => {
          e.stopPropagation();
        },
        { passive: true }
      );
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        updateSlider(currentIndex + 1);
      });

      // Prevent touch events from blocking scroll
      nextBtn.addEventListener(
        'touchstart',
        (e) => {
          e.stopPropagation();
        },
        { passive: true }
      );
    }

    // Dot controls
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => updateSlider(index));
    });

    // Touch controls with better scroll handling
    let startY = 0;
    let isHorizontalSwipe = null;

    const handleStart = (e) => {
      isDragging = true;
      isHorizontalSwipe = null; // Reset swipe direction
      startX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
      startY = e.type.includes('mouse') ? e.clientY : e.touches[0].clientY;
      track.style.transition = 'none';
    };

    const handleMove = (e) => {
      if (!isDragging) return;

      currentX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
      const currentY = e.type.includes('mouse') ? e.clientY : e.touches[0].clientY;

      const diffX = Math.abs(currentX - startX);
      const diffY = Math.abs(currentY - startY);

      // Determine swipe direction on first significant move
      if (isHorizontalSwipe === null && (diffX > 5 || diffY > 5)) {
        isHorizontalSwipe = diffX > diffY;
      }

      // Only handle horizontal swipes and prevent default for those
      if (isHorizontalSwipe === true) {
        e.preventDefault();
        const diff = currentX - startX;
        const offset = -currentIndex * 100 + (diff / container.offsetWidth) * 100;
        track.style.transform = `translateX(${offset}%)`;
      } else if (isHorizontalSwipe === false) {
        // It's a vertical swipe, stop tracking and allow scroll
        isDragging = false;
      }
    };

    const handleEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      track.style.transition = 'transform 0.3s ease';

      // Only process horizontal swipes
      if (isHorizontalSwipe === true) {
        const diff = currentX - startX;
        const threshold = container.offsetWidth * 0.2;

        if (Math.abs(diff) > threshold) {
          if (diff > 0 && currentIndex > 0) {
            updateSlider(currentIndex - 1);
          } else if (diff < 0 && currentIndex < slides.length - 1) {
            updateSlider(currentIndex + 1);
          } else {
            updateSlider(currentIndex);
          }
        } else {
          updateSlider(currentIndex);
        }
      } else {
        // Reset position if it was a vertical swipe
        updateSlider(currentIndex);
      }

      // Reset swipe direction
      isHorizontalSwipe = null;
    };

    // Add event listeners
    track.addEventListener('touchstart', handleStart, { passive: false });
    track.addEventListener('touchmove', handleMove, { passive: false });
    track.addEventListener('touchend', handleEnd);
    track.addEventListener('mousedown', handleStart);
    track.addEventListener('mousemove', handleMove);
    track.addEventListener('mouseup', handleEnd);
    track.addEventListener('mouseleave', handleEnd);

    // Initialize
    updateSlider(0);
    container.style.display = 'block';
    container.style.visibility = 'visible';
  }

  initSizeGuideModal() {
    // Size guide modal
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-size-guide-trigger]');
      if (trigger) {
        const modal = document.getElementById('sizeGuideModal');
        if (modal) {
          modal.style.display = 'flex';
          // Trigger reflow to enable transition
          void modal.offsetWidth;
          modal.classList.add('is-active');
          document.body.style.overflow = 'hidden';
        }
      }
    });

    // Close size guide modal
    document.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('[data-size-guide-close]');
      if (closeBtn) {
        const modal = document.getElementById('sizeGuideModal');
        if (modal) {
          modal.classList.remove('is-active');
          setTimeout(() => {
            modal.style.display = 'none';
            document.body.style.overflow = '';
          }, 300);
        }
      }
    });

    // Close on overlay click
    document.addEventListener('click', (e) => {
      const modal = document.getElementById('sizeGuideModal');
      if (e.target === modal && modal) {
        modal.classList.remove('is-active');
        setTimeout(() => {
          modal.style.display = 'none';
          document.body.style.overflow = '';
        }, 300);
      }
    });

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const modal = document.getElementById('sizeGuideModal');
        if (modal && modal.classList.contains('is-active')) {
          modal.classList.remove('is-active');
          setTimeout(() => {
            modal.style.display = 'none';
            document.body.style.overflow = '';
          }, 300);
        }
      }
    });
  }

  initStockNotifyModal() {
    const modal = document.getElementById('stockNotifyModal');
    if (!modal) return;

    // Close buttons
    modal.querySelectorAll('[data-notify-close]').forEach((btn) => {
      btn.addEventListener('click', () => {
        modal.style.display = 'none';
        document.body.style.overflow = '';
      });
    });

    // Form submission
    const form = modal.querySelector('[data-notify-form]');
    const messageDiv = modal.querySelector('.stock-notify-form__message');

    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const email = form.email.value;
        const variantId = form.variant_id.value;
        const productId = form.product_id.value;

        // Show loading state
        const submitBtn = form.querySelector('.stock-notify-form__submit');
        const originalText = submitBtn.textContent;
        submitBtn.textContent = 'SENDING...';
        submitBtn.disabled = true;

        try {
          // Here you would send the notification request to your backend
          // For now, just show success message
          await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate API call

          messageDiv.textContent = "Thank you! We'll notify you when this item is back in stock.";
          messageDiv.className = 'stock-notify-form__message success';
          messageDiv.style.display = 'block';

          form.style.display = 'none';

          // Close modal after 3 seconds
          setTimeout(() => {
            modal.style.display = 'none';
            document.body.style.overflow = '';
            // Reset form
            form.reset();
            form.style.display = 'block';
            messageDiv.style.display = 'none';
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          }, 3000);
        } catch (error) {
          messageDiv.textContent = 'Something went wrong. Please try again.';
          messageDiv.className = 'stock-notify-form__message error';
          messageDiv.style.display = 'block';
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }
      });
    }
  }

  checkInitialVariant() {
    const variantInput = this.form?.querySelector('input[name="id"]');
    if (!variantInput) {
      return;
    }

    const variants = window.productVariants || [];
    const shouldPreselect = window.preselectVariant === true;

    if (!shouldPreselect) {
      variantInput.value = '';
      this.currentVariant = null;
      this.updateSizeButtonStates();
      return;
    }

    const initialVariantId = window.initialVariantId || variantInput.value;
    let currentVariant = variants.find((v) => v.id == initialVariantId);

    if (!currentVariant) {
      currentVariant = variants[0];
    }

    if (currentVariant && variantInput.value != currentVariant.id) {
      variantInput.value = currentVariant.id;
    }

    if (currentVariant) {
      this.currentVariant = currentVariant;

      this.updateAvailability(currentVariant);

      if (currentVariant.options) {
        currentVariant.options.forEach((option, index) => {
          const input = this.querySelector(`input[data-option-position="${index}"][data-option-value="${option}"]`);
          if (input) {
            input.checked = true;
          }
        });

        const sizeLabel = this.querySelector('[data-selected-size]');
        if (sizeLabel && currentVariant.options[1]) {
          sizeLabel.textContent = currentVariant.options[1];
        }
      }

      this.updatePrice(currentVariant);
      this.updateStockInfo(currentVariant);
      this.updateSku(currentVariant);
    }
  }

  onVariantChange(event) {
    // Support both button clicks and radio input changes
    const target = event.currentTarget;
    const input = target.type === 'radio' ? target : target.querySelector('input[type="radio"]');

    if (!input) return;

    const position = input.dataset.optionPosition;
    const value = input.dataset.optionValue;

    // Check the radio input (this will trigger native checked state)
    if (!input.checked) {
      input.checked = true;
    }

    // Update size label display
    const sizeLabel = this.querySelector('[data-selected-size]');
    if (sizeLabel) {
      sizeLabel.textContent = value;
    }

    // Find matching variant
    this.updateVariant();
  }

  updateVariant() {
    const selectedOptions = [];
    const variants = window.productVariants || [];

    // Get all selected options from radio inputs
    this.querySelectorAll('.product-option__input:checked').forEach((input) => {
      const position = parseInt(input.dataset.optionPosition);
      selectedOptions[position] = input.dataset.optionValue;
    });

    // CRITICAL: Fill in missing options (hidden single-value options)
    // Get the number of options from first variant
    if (variants.length > 0 && variants[0].options) {
      const totalOptions = variants[0].options.length;
      
      for (let i = 0; i < totalOptions; i++) {
        if (!selectedOptions[i]) {
          // Find all inputs for this position
          const inputsForPosition = this.querySelectorAll(`input[data-option-position="${i}"]`);
          
          if (inputsForPosition.length === 0) {
            // No inputs = hidden option, get value from first variant
            selectedOptions[i] = variants[0].options[i];
          } else if (inputsForPosition.length === 1) {
            // Single option = auto-select it
            selectedOptions[i] = inputsForPosition[0].dataset.optionValue;
            inputsForPosition[0].checked = true;
          } else {
            // Multiple options but none selected = use first
            selectedOptions[i] = inputsForPosition[0].dataset.optionValue;
          }
        }
      }
    }

    // Update disabled state for size buttons based on availability
    this.updateSizeButtonStates();

    // Find variant that matches selected options
    const variant = variants.find((v) => {
      const matches = v.options.every((option, index) => {
        return option === selectedOptions[index];
      });
      return matches;
    });

    if (variant) {
      this.currentVariant = variant;
      this.updateVariantInput(variant.id);
      this.updatePrice(variant);

      // IMMEDIATELY update availability - no delay needed!
      this.updateAvailability(variant);

      // Update URL, stock info, and SKU
      this.updateURL(variant);
      this.updateStockInfo(variant);
      this.updateSku(variant);
      
      // Hide any warning message if visible
      const warningMsg = this.querySelector('.variant-warning-message');
      if (warningMsg) {
        warningMsg.style.display = 'none';
      }
    }
  }

  updateVariantInput(variantId) {
    const input = this.form.querySelector('input[name="id"]');
    if (input) {
      input.value = variantId;
    }
  }

  updateSku(variant) {
    const skuEl = this.querySelector('[data-product-sku]');
    if (!skuEl) return;
    skuEl.textContent = variant.sku ? `SKU: ${variant.sku}` : `SKU: ${skuEl.dataset.baseSku || ''}`;
  }

  updatePrice(variant) {
    const priceWrapper = this.querySelector('.product-detail-modern__price-wrapper');
    if (!priceWrapper || !variant.price) return;

    const priceElement = priceWrapper.querySelector('[data-price]');
    const comparePriceElement = priceWrapper.querySelector('[data-compare-price]');
    const discountBadge = priceWrapper.querySelector('[data-discount-badge]');

    if (!priceElement) return;

    // Format current price
    const formattedPrice = this.formatMoney(variant.price);

    // Check if variant has compare at price (on sale)
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const formattedComparePrice = this.formatMoney(variant.compare_at_price);
      const discountPercentage = Math.round(
        ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
      );

      // Update price with sale class
      priceElement.textContent = formattedPrice;
      priceElement.classList.add('product-detail-modern__price--on-sale');

      // Show or create compare price
      if (comparePriceElement) {
        comparePriceElement.textContent = formattedComparePrice;
        comparePriceElement.style.display = '';
      } else {
        const newComparePrice = document.createElement('span');
        newComparePrice.className = 'product-detail-modern__price-compare';
        newComparePrice.setAttribute('data-compare-price', '');
        newComparePrice.textContent = formattedComparePrice;
        priceElement.after(newComparePrice);
      }

      // Show or create discount badge
      if (discountBadge) {
        discountBadge.textContent = `-${discountPercentage}%`;
        discountBadge.style.display = '';
      } else {
        const newBadge = document.createElement('span');
        newBadge.className = 'product-detail-modern__price-badge';
        newBadge.setAttribute('data-discount-badge', '');
        newBadge.textContent = `-${discountPercentage}%`;
        (comparePriceElement || priceElement).after(newBadge);
      }
    } else {
      // No sale - regular price
      priceElement.textContent = formattedPrice;
      priceElement.classList.remove('product-detail-modern__price--on-sale');

      // Hide compare price and badge
      if (comparePriceElement) {
        comparePriceElement.style.display = 'none';
      }
      if (discountBadge) {
        discountBadge.style.display = 'none';
      }
    }
  }

  updateSizeButtonStates() {
    // Get all option inputs (radio buttons)
    const optionInputs = this.querySelectorAll('.product-option__input');

    optionInputs.forEach((input) => {
      const label = this.querySelector(`label[for="${input.id}"]`);
      if (!label) return;

      const optionValue = input.dataset.optionValue;
      const position = parseInt(input.dataset.optionPosition);

      // Find if any variant with this option is available
      let isAvailable = false;

      if (window.productVariants) {
        window.productVariants.forEach((variant) => {
          if (variant.options[position] === optionValue && variant.available === true) {
            isAvailable = true;
          }
        });
      }

      // Update disabled state
      if (isAvailable) {
        input.disabled = false;
        label.removeAttribute('data-disabled');
      } else {
        input.disabled = true;
        label.setAttribute('data-disabled', 'true');
      }
    });
  }

  updateAvailability(variant) {
    // Find add to cart button more reliably
    const addToCartButton =
      this.querySelector('.product-detail-modern__add-to-cart-inline') ||
      this.querySelector('.product-detail-modern__add-to-cart') ||
      this.querySelector('button[name="add"]') ||
      this.querySelector('button[type="submit"]');

    const buyNowButton = this.querySelector('.product-detail-modern__buy-now');

    if (addToCartButton) {
      // Store original text if not already stored
      if (!addToCartButton.dataset.originalText) {
        addToCartButton.dataset.originalText = addToCartButton.textContent.trim();
      }

      // Create bound handler if it doesn't exist
      if (!this.boundHandleNotifyClick) {
        this.boundHandleNotifyClick = this.handleNotifyClick.bind(this);
      }

      // Check availability and update button accordingly
      if (variant.available) {
        // Enable add to cart for available variants
        addToCartButton.disabled = false;

        // FORCE text update - remove any child elements and set text content directly
        const buttonText = addToCartButton.dataset.originalText || 'ADD TO CART';
        addToCartButton.replaceChildren(document.createTextNode(buttonText));

        // Update button attributes for available variant
        addToCartButton.type = 'submit';
        addToCartButton.classList.remove('product-detail-modern__notify-button');
        addToCartButton.name = 'add';

        // Reset inline styles
        addToCartButton.style.background = '';
        addToCartButton.style.color = '';

        // Remove notify handler and restore submit handler
        addToCartButton.removeEventListener('click', this.boundHandleNotifyClick);
        addToCartButton.type = 'submit';
        addToCartButton.name = 'add';

        if (buyNowButton) buyNowButton.disabled = false;
      } else {
        // Show notify button for out of stock variants
        addToCartButton.disabled = false;

        // FORCE text update - remove any child elements and set text content directly
        addToCartButton.replaceChildren(document.createTextNode('NOTIFY WHEN AVAILABLE'));

        // Update button attributes for unavailable variant
        addToCartButton.type = 'button';
        addToCartButton.classList.add('product-detail-modern__notify-button');

        // Keep same style as add to cart
        addToCartButton.style.background = '';
        addToCartButton.style.color = '';

        // Change button type and add notify handler
        addToCartButton.type = 'button';
        addToCartButton.removeEventListener('click', this.boundHandleNotifyClick);
        addToCartButton.addEventListener('click', this.boundHandleNotifyClick);

        if (buyNowButton) buyNowButton.disabled = true;
      }
    }

    // Update stock info
    this.updateStockInfo(variant);
  }

  handleNotifyClick(e) {
    e.preventDefault();
    e.stopPropagation();

    // Show notify modal
    const modal = document.getElementById('stockNotifyModal');
    if (modal) {
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';

      // Store current variant info
      const variantInput = modal.querySelector('[name="variant_id"]');
      const variantTitle = modal.querySelector('.stock-notify-variant-title');

      if (variantInput && this.currentVariant) {
        variantInput.value = this.currentVariant.id;
      }

      if (variantTitle && this.currentVariant) {
        variantTitle.textContent = this.currentVariant.title || 'Selected Variant';
      }
    }
  }

  updateStockInfo(variant) {
    let inventoryBlock = this.querySelector('.product-detail-modern__inventory-block');
    let stockInfo = this.querySelector('.product-detail-modern__stock-info');

    // If inventory block doesn't exist, create it
    if (!inventoryBlock) {
      this.createInventoryBlock();
      inventoryBlock = this.querySelector('.product-detail-modern__inventory-block');
      stockInfo = this.querySelector('.product-detail-modern__stock-info');
    }

    if (!inventoryBlock || !stockInfo) {
      return;
    }

    const threshold = parseInt(inventoryBlock.dataset.inventoryThreshold) || 9;
    const stockTextTemplate = inventoryBlock.dataset.lowStockText || 'Only {quantity} left in stock. Order soon.';

    // Get inventory quantity - handle different possible property names
    let inventoryQuantity = variant.inventory_quantity;
    if (inventoryQuantity === undefined || inventoryQuantity === null) {
      inventoryQuantity = variant.inventory_qty;
    }
    if (inventoryQuantity === undefined || inventoryQuantity === null) {
      inventoryQuantity = variant.quantity;
    }

    // If still no inventory data, try to get from initial DOM data
    if (inventoryQuantity === undefined || inventoryQuantity === null) {
      inventoryQuantity = parseInt(inventoryBlock.dataset.initialInventory) || 0;
    }

    // Show stock info if inventory is low and variant is available
    if (variant.available && inventoryQuantity > 0 && inventoryQuantity <= threshold) {
      inventoryBlock.style.display = 'block';
      inventoryBlock.style.visibility = 'visible';
      stockInfo.style.display = 'flex';
      const span = stockInfo.querySelector('[data-stock-text]');
      if (span) {
        span.textContent = stockTextTemplate.replace('{quantity}', inventoryQuantity);
      }
    } else {
      inventoryBlock.style.display = 'none';
    }
  }

  updateURL(variant) {
    if (!variant || !variant.id) return;

    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  changeQuantity(delta) {
    const input = this.querySelector('[data-quantity-input]');
    if (!input) return;

    const currentValue = parseInt(input.value) || 1;
    const newValue = Math.max(1, currentValue + delta);

    input.value = newValue;
    this.updateQuantityButtons();
  }

  updateQuantityButtons() {
    const input = this.querySelector('[data-quantity-input]');
    const minusButton = this.querySelector('[data-quantity-minus]');

    if (input && minusButton) {
      const currentValue = parseInt(input.value) || 1;
      minusButton.disabled = currentValue <= 1;
    }
  }

  async onFormSubmit(event) {
    event.preventDefault();

    // Double check current variant is set correctly
    const variantInput = this.form.querySelector('input[name="id"]');
    
    if (!variantInput || !variantInput.value) {
      
      // Show warning message under size selector
      const variantBlock = this.querySelector('.product-detail-modern__variant-block');
      if (variantBlock) {
        let warningMsg = variantBlock.querySelector('.variant-warning-message');
        
        if (!warningMsg) {
          warningMsg = document.createElement('div');
          warningMsg.className = 'variant-warning-message';
          warningMsg.textContent = 'Please select a size';
          variantBlock.appendChild(warningMsg);
        }
        
        // Show with animation
        warningMsg.style.display = 'block';
        
        // Remove after 3 seconds
        setTimeout(() => {
          warningMsg.style.display = 'none';
        }, 3000);
      }
      
      return;
    }

    const submitButton =
      this.querySelector('.product-detail-modern__add-to-cart-inline') ||
      this.querySelector('.product-detail-modern__add-to-cart');
    
    if (!submitButton) {
      return;
    }

    // Check if already processing
    if (submitButton.hasAttribute('disabled') || submitButton.classList.contains('adding-to-cart')) {
      return;
    }

    // Show loading state
    submitButton.classList.add('adding-to-cart');
    submitButton.disabled = true;
    const originalText = submitButton.getAttribute('data-original-text') || submitButton.textContent;
    submitButton.textContent = 'ADDING...';

    try {
      const formData = new FormData(this.form);

      // Ensure we're using the correct variant ID
      if (this.currentVariant && this.currentVariant.id) {
        formData.set('id', this.currentVariant.id.toString());
      }

      // Add headers for proper JSON response
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      // Handle both success and error responses
      const responseData = await response.json();
      
      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 422) {
          // Stock limit or validation error
          throw new Error(responseData.message || responseData.description || 'Cannot add item to cart');
        } else {
          throw new Error(responseData.message || 'Failed to add to cart');
        }
      }

      // Update cart UI
      this.updateCartUI(responseData);

      // Show success message
      submitButton.textContent = 'ADDED TO CART ✓';
      submitButton.classList.remove('adding-to-cart');
      submitButton.classList.add('product-detail-modern__add-to-cart--success');

      // Reset button after delay
      setTimeout(() => {
        submitButton.disabled = false;
        submitButton.textContent = originalText;
        submitButton.classList.remove('product-detail-modern__add-to-cart--success');
      }, 2000);
    } catch (error) {
      // Show user-friendly error message
      const errorMessage = error.message || 'Error adding to cart';
      
      // Display error message under the button
      const variantBlock = this.querySelector('.product-detail-modern__variant-block');
      if (variantBlock) {
        let errorMsg = variantBlock.querySelector('.variant-error-message');
        
        if (!errorMsg) {
          errorMsg = document.createElement('div');
          errorMsg.className = 'variant-error-message';
          variantBlock.appendChild(errorMsg);
        }
        
        errorMsg.textContent = errorMessage;
        errorMsg.style.display = 'block';
        
        // Hide error after 5 seconds
        setTimeout(() => {
          errorMsg.style.display = 'none';
        }, 5000);
      }
      
      // Reset button state (no error text on button)
      submitButton.classList.remove('adding-to-cart');
      submitButton.disabled = false;
      submitButton.textContent = originalText;
    }
  }

  onBuyNow(event) {
    event.preventDefault();

    // Add return_to parameter for checkout redirect
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'return_to';
    input.value = '/checkout';
    this.form.appendChild(input);

    // Submit form
    this.form.submit();
  }

  updateCartUI(item) {
    // Dispatch custom event for cart updates
    document.dispatchEvent(
      new CustomEvent('cart:add', {
        detail: { item },
      })
    );

    // Update cart count in header
    if (document.querySelector('[data-header-cart-count]')) {
      fetch('/cart.js')
        .then((res) => res.json())
        .then((cart) => {
          window.SEMUHeader?.renderCartCount?.(cart.item_count, { animate: true });
        });
    }
  }

  formatMoney(cents) {
    const format = window.theme?.settings?.currency_display_format || 'symbol_only';

    // Shopify.formatMoney yoksa manuel format
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney && window.theme?.moneyFormat) {
      let formatted;

      switch (format) {
        case 'code_only':
          // Get formatted price and remove symbols
          const temp = Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
          const currency = window.theme?.currency || 'USD';
          // Remove common currency symbols like in price.liquid
          const noSymbol = temp.replace(/[$£€¥₺₹¢₽₩]/g, '').trim();
          formatted = `${noSymbol} ${currency}`;
          break;

        case 'symbol_and_code':
          formatted = Shopify.formatMoney(
            cents,
            window.theme?.moneyWithCurrencyFormat ||
              `${window.theme?.moneyFormat || '${{amount}}'} ${window.theme?.currency || 'USD'}`
          );
          break;

        case 'symbol_only':
        default:
          formatted = Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
          break;
      }

      return formatted;
    }

    // Fallback - manuel formatting
    const amount = (cents / 100).toFixed(2);
    const currency = window.theme?.currency || 'USD';
    const moneyFormat = window.theme?.moneyFormat || '${{amount}}';

    switch (format) {
      case 'code_only':
        return `${amount} ${currency}`;
      case 'symbol_and_code':
        // Try to extract symbol from moneyFormat
        const symbol = moneyFormat.match(/[£$€¥₺₹¢₽₩]/)?.[0] || '$';
        return `${symbol}${amount} ${currency}`;
      case 'symbol_only':
      default:
        // Use moneyFormat pattern
        if (moneyFormat.includes('{{amount}}')) {
          return moneyFormat.replace('{{amount}}', amount);
        } else {
          // Default to common format
          const symbol = moneyFormat.match(/[£$€¥₺₹¢₽₩]/)?.[0] || '$';
          return `${symbol}${amount}`;
        }
    }
  }
}

// Define custom element
customElements.define('product-detail-modern', ProductDetailModern);
/**
 * Complete the Look Drawer
 * Handles product combine drawer functionality
 */

class CompleteTheLookDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawer = this;
    this.overlay = this.querySelector('[data-ctld-overlay]');
    this.closeButton = this.querySelector('[data-ctld-close]');
    this.content = this.querySelector('[data-ctld-content]');
    this.products = [];
    this.glideInstances = [];

    this.bindEvents();
  }

  bindEvents() {
    // Close events
    this.closeButton?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
      }
    });

    // Open drawer event
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-complete-the-look-trigger]');
      if (trigger) {
        e.preventDefault();
        this.open();
      }
    });
  }

  async open() {
    // Get product handles from data
    const dataContainer = document.querySelector('.complete-the-look-data');
    if (!dataContainer) {
      return;
    }

    const handles = Array.from(dataContainer.querySelectorAll('[data-product-handle]')).map(
      (el) => el.dataset.productHandle
    );

    if (handles.length === 0) {
      return;
    }

    // Show drawer
    this.removeAttribute('hidden');
    this.style.display = '';
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Load products
    try {
      await this.loadProducts(handles);
    } catch (error) {
      this.showError();
    }
  }

  close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';

    // Hide drawer after transition
    setTimeout(() => {
      if (!this.classList.contains('is-open')) {
        this.setAttribute('hidden', '');
        this.style.display = 'none';
      }
    }, 350); // Match transition duration

    // Destroy all Glide instances
    this.glideInstances.forEach((glide) => {
      if (glide) glide.destroy();
    });
    this.glideInstances = [];

    // Clear content after animation
    setTimeout(() => {
      this.content.innerHTML = '';
      this.products = [];
    }, 300);
  }

  async loadProducts(handles) {
    this.showLoading();
    this.products = [];

    // Fetch all products
    const promises = handles.map((handle) => this.fetchProduct(handle));
    const results = await Promise.allSettled(promises);

    // Filter successful results
    this.products = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);

    if (this.products.length === 0) {
      this.showError('No products found');
      return;
    }

    // Render products
    this.renderProducts();
  }

  async fetchProduct(handle) {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) throw new Error(`Product ${handle} not found`);
    return await response.json();
  }

  renderProducts() {
    let html = '';

    this.products.forEach((product, index) => {
      const variant = product.variants.find((v) => v.available) || product.variants[0];
      const sliderId = `ctld-slider-${index}`;

      html += `
        <div class="ctld-product" data-product-index="${index}">
          <!-- Image Slider - Matching product drawer -->
          <div class="ctld-product__slider-wrapper">
            <div class="glide ctld-product__media-glide" id="${sliderId}">
              <div class="glide__track" data-glide-el="track">
                <ul class="glide__slides">
                  ${product.images
                    .map(
                      (image) => `
                    <li class="glide__slide" data-media-type="image">
                      <div class="media media--portrait">
                        <img
                          src="${image}?width=720&quality=80"
                          alt="${product.title}"
                          loading="lazy"
                          width="720"
                          height="720"
                        />
                      </div>
                    </li>
                  `
                    )
                    .join('')}
                </ul>
              </div>
              ${
                product.images.length > 1
                  ? `
              <div class="glide__arrows" data-glide-el="controls">
                <button class="glide__arrow glide__arrow--left" data-glide-dir="<">‹</button>
                <button class="glide__arrow glide__arrow--right" data-glide-dir=">">›</button>
              </div>
              <div class="glide__bullets" data-glide-el="controls[nav]">
                ${product.images
                  .map((_, i) => `<button class="glide__bullet" data-glide-dir="=${i}"></button>`)
                  .join('')}
              </div>
              `
                  : ''
              }
            </div>
          </div>

          <!-- Product Content -->
          <div class="ctld-product__content">
            ${product.vendor ? `<div class="ctld-product__vendor">${product.vendor}</div>` : ''}
            <h3 class="ctld-product__title">
              <a href="/products/${product.handle}">${product.title}</a>
            </h3>
            <div class="ctld-product__price" data-product-price="${index}">
              ${this.formatPrice(variant)}
            </div>

            <!-- Variants -->
            ${this.renderVariants(product, index, variant.id)}

            <!-- Add to Cart Button -->
            <button
              type="button"
              class="ctld-product__add-button"
              data-product-add="${index}"
              data-product-id="${product.id}"
              data-variant-id="${variant.id}"
              ${!variant.available ? 'disabled' : ''}
            >
              ${variant.available ? 'ADD TO CART' : 'SOLD OUT'}
            </button>
          </div>
        </div>
      `;
    });

    this.content.innerHTML = html;

    // Initialize Glide sliders
    this.initializeSliders();

    // Bind events
    this.bindProductEvents();
  }

  renderVariants(product, productIndex, selectedVariantId) {
    // Check if product has multiple variants
    if (product.variants.length <= 1 || !product.options) {
      return '';
    }

    let html = '<div class="ctld-product__variants">';

    product.options.forEach((option, optionIndex) => {
      const optionName = typeof option === 'string' ? option : option.name || `Option ${optionIndex + 1}`;

      // Translate Turkish to English
      const translations = {
        Beden: 'Size',
        Renk: 'Color',
        Ebat: 'Size',
        Boyut: 'Size',
      };
      const translatedName = translations[optionName] || optionName;

      // Skip "Title" option
      if (translatedName.toLowerCase() === 'title') return;

      // Get unique values for this option
      const values = [...new Set(product.variants.map((v) => v.options[optionIndex]))];

      // Only show option if it has more than one value (skip single options)
      if (values.length > 1 && values[0] !== 'Default Title') {
        html += `
          <fieldset class="ctld-product__variant-options">
            <legend>${translatedName}</legend>
            <div class="ctld-product__variant-buttons">
              ${values
                .map((value) => {
                  const variant = product.variants.find((v) => v.options[optionIndex] === value);
                  const isAvailable = this.isOptionValueAvailable(product, optionIndex, value);
                  const isSelected = variant && variant.id === selectedVariantId;

                  return `
                  <button
                    type="button"
                    class="ctld-product__variant-button ${isSelected ? 'is-selected' : ''}"
                    data-product-variant="${productIndex}"
                    data-option-index="${optionIndex}"
                    data-option-value="${value}"
                    ${!isAvailable ? 'disabled' : ''}
                  >
                    ${value}
                  </button>
                `;
                })
                .join('')}
            </div>
          </fieldset>
        `;
      }
    });

    html += '</div>';
    return html;
  }

  isOptionValueAvailable(product, optionIndex, value) {
    return product.variants.some((variant) => variant.options[optionIndex] === value && variant.available);
  }

  formatPrice(variant) {
    const formatMoney = (cents) => {
      if (window.Shopify && Shopify.formatMoney) {
        const moneyFormat = window.theme?.moneyFormat || '${{amount}}';
        return Shopify.formatMoney(cents, moneyFormat);
      }
      return `$${(cents / 100).toFixed(2)}`;
    };

    if (variant.compare_at_price > variant.price) {
      return `
        <span class="price__sale">${formatMoney(variant.price)}</span>
        <span class="price__regular">${formatMoney(variant.compare_at_price)}</span>
      `;
    }

    return `<span>${formatMoney(variant.price)}</span>`;
  }

  initializeSliders() {
    this.products.forEach((product, index) => {
      const sliderId = `ctld-slider-${index}`;
      const sliderElement = document.getElementById(sliderId);

      if (sliderElement && window.Glide) {
        const glide = new Glide(sliderElement, {
          type: 'slider',
          perView: 2.05,
          gap: 10,
          peek: 0,
          bound: true,
          rewind: false,
          focusAt: 0,
          breakpoints: {
            750: {
              perView: 1.5,
              gap: 0,
            },
          },
        });

        glide.mount();
        this.glideInstances.push(glide);
      }
    });
  }

  bindProductEvents() {
    // Variant selection
    this.content.querySelectorAll('[data-product-variant]').forEach((button) => {
      button.addEventListener('click', (e) => {
        const productIndex = parseInt(e.currentTarget.dataset.productVariant);
        const optionIndex = parseInt(e.currentTarget.dataset.optionIndex);
        const optionValue = e.currentTarget.dataset.optionValue;

        this.handleVariantChange(productIndex, optionIndex, optionValue);
      });
    });

    // Add to cart
    this.content.querySelectorAll('[data-product-add]').forEach((button) => {
      button.addEventListener('click', async (e) => {
        const productIndex = parseInt(e.currentTarget.dataset.productAdd);
        const variantId = parseInt(e.currentTarget.dataset.variantId);

        await this.addToCart(productIndex, variantId, button);
      });
    });
  }

  handleVariantChange(productIndex, optionIndex, optionValue) {
    const product = this.products[productIndex];
    if (!product) return;

    // Get currently selected options
    const productElement = this.content.querySelector(`[data-product-index="${productIndex}"]`);
    const selectedOptions = [];

    product.options.forEach((option, index) => {
      const buttons = productElement.querySelectorAll(`[data-option-index="${index}"]`);
      const selectedButton = Array.from(buttons).find((btn) => btn.classList.contains('is-selected'));

      if (index === optionIndex) {
        selectedOptions[index] = optionValue;
      } else if (selectedButton) {
        selectedOptions[index] = selectedButton.dataset.optionValue;
      } else {
        selectedOptions[index] = product.variants[0].options[index];
      }
    });

    // Find matching variant
    const variant = product.variants.find((v) => v.options.every((opt, idx) => opt === selectedOptions[idx]));

    if (!variant) return;

    // Update UI
    productElement.querySelectorAll('.ctld-product__variant-button').forEach((btn) => {
      btn.classList.remove('is-selected');
    });
    productElement
      .querySelector(`[data-option-index="${optionIndex}"][data-option-value="${optionValue}"]`)
      ?.classList.add('is-selected');

    // Update price
    const priceElement = productElement.querySelector('[data-product-price]');
    if (priceElement) {
      priceElement.innerHTML = this.formatPrice(variant);
    }

    // Update add button
    const addButton = productElement.querySelector('[data-product-add]');
    if (addButton) {
      addButton.dataset.variantId = variant.id;
      addButton.disabled = !variant.available;
      addButton.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    }
  }

  async addToCart(productIndex, variantId, button) {
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const item = await response.json();

      // Publish cart update event
      document.dispatchEvent(
        new CustomEvent('cart:add', {
          detail: { item },
        })
      );

      // Show success
      button.textContent = 'Added ✓';
      setTimeout(() => {
        button.textContent = 'Add to Cart';
        button.disabled = false;
      }, 2000);

      // Update cart count
      fetch('/cart.js')
        .then((res) => res.json())
        .then((cart) => {
          window.SEMUHeader?.renderCartCount?.(cart.item_count, { animate: true });
        });
    } catch (error) {
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = 'Add to Cart';
        button.disabled = false;
      }, 2000);
    }
  }

  showLoading() {
    this.content.innerHTML = `
      <div class="complete-the-look-drawer__loading">
        <div class="complete-the-look-drawer__spinner"></div>
        <p>Loading products...</p>
      </div>
    `;
  }

  showError(message = 'Failed to load products') {
    this.content.innerHTML = `
      <div class="complete-the-look-drawer__loading">
        <p style="color: #e74c3c;">${message}</p>
      </div>
    `;
  }
}

// Register custom element
customElements.define('complete-the-look-drawer', CompleteTheLookDrawer);

// Size Guide Accordion Handler
document.addEventListener('DOMContentLoaded', function () {
  // Handle accordion clicks in size guide
  document.addEventListener('click', function (e) {
    const accordionTitle = e.target.closest('.BodyAccordion .item > .accordion-title');
    if (accordionTitle) {
      const item = accordionTitle.parentElement;
      item.classList.toggle('Active');
    }
  });
});
